'use client'

import React, { useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { cn } from './utils'

export interface DrawerProps {
  isOpen: boolean
  onClose: () => void
  children: React.ReactNode
  title?: string
  position?: 'left' | 'right'
  className?: string
  animate?: boolean
}

export const Drawer: React.FC<DrawerProps> = ({
  isOpen,
  onClose,
  children,
  title,
  position = 'right',
  className,
  animate = true
}) => {
  const shouldAnimate = animate !== false
  useEffect(() => {
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose()
    }

    if (isOpen) {
      document.addEventListener('keydown', handleEscape)
      document.body.style.overflow = 'hidden'
    }

    return () => {
      document.removeEventListener('keydown', handleEscape)
      document.body.style.overflow = 'unset'
    }
  }, [isOpen, onClose])

  const drawerContent = isOpen ? (
    <>
      <motion.div
        {...(shouldAnimate
          ? { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.2 } }
          : {})}
        className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm"
        onClick={onClose}
      />
      <motion.div
        {...(shouldAnimate
          ? {
              initial: { x: position === 'left' ? '-100%' : '100%' },
              animate: { x: 0 },
              exit: { x: position === 'left' ? '-100%' : '100%' },
              transition: { type: 'spring', damping: 30, stiffness: 300 }
            }
          : {})}
        className={cn(
          'fixed top-0 z-50 h-full w-[400px] sm:w-[480px] bg-background shadow-xl',
          position === 'left' ? 'left-0 border-r' : 'right-0 border-l',
          'border-border',
          className
        )}
      >
        {title && (
          <div className="flex items-center justify-between px-6 py-4 border-b border-border">
            <h2 className="text-xl font-semibold text-foreground">{title}</h2>
            <button
              onClick={onClose}
              className="px-3 py-1.5 rounded-md border border-border text-sm font-medium text-foreground hover:bg-secondary transition-colors"
              aria-label="Close drawer"
            >
              Esc
            </button>
          </div>
        )}
        <div className="overflow-y-auto h-[calc(100%-5rem)] p-6">
          {children}
        </div>
      </motion.div>
    </>
  ) : null

  return shouldAnimate ? <AnimatePresence>{drawerContent}</AnimatePresence> : drawerContent
}
